import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Card from '../ui/Card'
import { vendorsAPI, VendorOpportunity } from '../../lib/services'

interface OpportunitiesInboxProps {
  vendorId: number
}

const STATUS_COLORS: Record<string, string> = {
  recommended: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  invited: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  under_consideration: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  selected: 'bg-green-500/10 text-green-600 border-green-500/20',
  contracted: 'bg-green-600/10 text-green-700 border-green-600/20',
  declined: 'bg-muted text-muted-foreground border-border',
  rejected: 'bg-red-500/10 text-red-600 border-red-500/20',
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return ''
  const diff = Date.now() - new Date(dateStr).getTime()
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  return `${months}mo ago`
}

export default function OpportunitiesInbox({ vendorId }: OpportunitiesInboxProps) {
  const [opportunities, setOpportunities] = useState<VendorOpportunity[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    vendorsAPI.getOpportunities(vendorId)
      .then(setOpportunities)
      .catch(err => console.error('Failed to load opportunities:', err))
      .finally(() => setLoading(false))
  }, [vendorId])

  return (
    <Card className="p-6">
      <h3 className="text-base font-semibold text-foreground mb-4">Opportunities</h3>

      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading...</div>
      ) : opportunities.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8 text-center">
          No opportunities yet. You'll see programs here when you're recommended.
        </div>
      ) : (
        <div className="overflow-x-auto -mx-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="pb-2 pl-6 pr-4 text-xs text-muted-foreground font-medium">Program</th>
                <th className="pb-2 px-4 text-xs text-muted-foreground font-medium">Product</th>
                <th className="pb-2 px-4 text-xs text-muted-foreground font-medium">Office</th>
                <th className="pb-2 px-4 text-xs text-muted-foreground font-medium">Status</th>
                <th className="pb-2 pl-4 pr-6 text-xs text-muted-foreground font-medium text-right">When</th>
              </tr>
            </thead>
            <tbody>
              {opportunities.map(opp => (
                <tr
                  key={opp.id}
                  onClick={() => navigate(`/submit/opportunities/${opp.id}`)}
                  className="border-b border-border/50 hover:bg-muted/50 transition-colors cursor-pointer"
                >
                  <td className="py-3 pl-6 pr-4 text-foreground font-medium">{opp.program_name || 'Unnamed'}</td>
                  <td className="py-3 px-4 text-muted-foreground">{opp.product_name || '—'}</td>
                  <td className="py-3 px-4 text-muted-foreground">{opp.office_name || '—'}</td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex px-2 py-0.5 text-xs rounded-full border ${STATUS_COLORS[opp.status] || 'bg-muted text-muted-foreground border-border'}`}>
                      {opp.status.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="py-3 pl-4 pr-6 text-muted-foreground text-right text-xs">
                    {timeAgo(opp.recommended_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}
